// Pure logic behind the catering lane. No React, no network — covered by
// cateringKdsLogic.test.ts. The server (catering_kds.go) decides which tickets
// exist; this only arranges and words them.

import { CK } from './cateringKdsStrings'
import type { CateringLineStatus, CateringOnKdsSetting, CateringTicket } from './cateringKdsTypes'

export const CATERING_KDS_KEY = 'kitchen_flow.catering_on_kds'
export const DEFAULT_DAYS_BEFORE = 1
export const MAX_DAYS_BEFORE = 7

/** The tenant's setting, read from the settings map. Anything unreadable is off. */
export function parseCateringOnKds(all: Record<string, unknown> | undefined): CateringOnKdsSetting {
  const off = { enabled: false, show_days_before: DEFAULT_DAYS_BEFORE }
  if (!all) return off
  const v = all[CATERING_KDS_KEY]
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return off
  const r = v as { enabled?: unknown; show_days_before?: unknown }
  const d = typeof r.show_days_before === 'number' && Number.isInteger(r.show_days_before)
    ? Math.min(MAX_DAYS_BEFORE, Math.max(0, r.show_days_before))
    : DEFAULT_DAYS_BEFORE
  return { enabled: r.enabled === true, show_days_before: d }
}

/** Both switches: the deploy runs the kitchen flow AND the admin turned the lane on. */
export function cateringOnKdsSwitchedOn(all: Record<string, unknown> | undefined): boolean {
  return !!all && all.kitchen_flow === true && parseCateringOnKds(all).enabled
}

export function ticketTitle(t: CateringTicket): string {
  return t.event_name?.trim() || t.title?.trim() || t.plan_no
}

export function readyCount(t: CateringTicket): number {
  return t.lines.filter((l) => l.status === 'ready').length
}

export function allReady(t: CateringTicket): boolean {
  return t.lines.length > 0 && t.lines.every((l) => l.status === 'ready')
}

/** A tap on a dish: ready ↔ not started. Cooking (long press) also goes to ready. */
export function nextLineStatus(s: CateringLineStatus): CateringLineStatus {
  return s === 'ready' ? 'sent' : 'ready'
}

/** Long press: cooking ↔ not started; a ready dish is left alone. */
export function longPressStatus(s: CateringLineStatus): CateringLineStatus | null {
  if (s === 'ready') return null
  return s === 'preparing' ? 'sent' : 'preparing'
}

/** The board's two groups: still to cook (most urgent first, as the server sent them) and done. */
export function splitTickets(ts: CateringTicket[]): { active: CateringTicket[]; done: CateringTicket[] } {
  return { active: ts.filter((t) => !t.done), done: ts.filter((t) => t.done) }
}

export type DueTier = 'later' | 'soon' | 'now' | 'late'

export interface DueInfo {
  label: string
  tier: DueTier
}

function localDateString(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function dayDiff(a: string, b: string): number {
  const toUTC = (s: string) => {
    const [y, m, d] = s.split('-').map(Number)
    return Date.UTC(y, m - 1, d)
  }
  return Math.round((toUTC(a) - toUTC(b)) / 86_400_000)
}

/** "2 h 10 min", "45 min", "3 days". */
export function spanText(ms: number): string {
  const min = Math.max(1, Math.round(Math.abs(ms) / 60_000))
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h < 24) return m === 0 || h >= 6 ? `${h} h` : `${h} h ${m} min`
  const days = Math.round(h / 24)
  return `${days} day${days === 1 ? '' : 's'}`
}

/**
 * The countdown a ticket shows. With a due time: overdue / due within 30 min
 * / within 2 h / later. Without one (a booking's time could not be read, or a
 * plan made by hand) the ticket is worded by its date.
 */
export function dueInfo(t: Pick<CateringTicket, 'due_at' | 'for_date' | 'days_away'>, now: Date = new Date()): DueInfo {
  if (t.due_at) {
    const ms = new Date(t.due_at).getTime() - now.getTime()
    if (ms < 0) return { label: CK.dueLate(spanText(ms)), tier: 'late' }
    if (ms <= 30 * 60_000) return { label: CK.dueIn(spanText(ms)), tier: 'now' }
    if (ms <= 2 * 3600_000) return { label: CK.dueIn(spanText(ms)), tier: 'soon' }
    if (ms <= 24 * 3600_000) return { label: CK.dueIn(spanText(ms)), tier: 'later' }
    const d = new Date(t.due_at)
    return { label: CK.dueAt(d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })), tier: 'later' }
  }
  // The server's count uses the business day boundary; the tablet's calendar
  // does not (just after midnight it is still "today" in the kitchen).
  const diff = typeof t.days_away === 'number' ? t.days_away : dayDiff(t.for_date, localDateString(now))
  if (diff < 0) return { label: CK.yesterday, tier: 'late' }
  if (diff === 0) return { label: CK.today, tier: 'soon' }
  if (diff === 1) return { label: CK.tomorrow, tier: 'later' }
  const [y, m, d] = t.for_date.split('-').map(Number)
  return { label: new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }), tier: 'later' }
}

/** Replace one ticket in the list with the server's fresh copy (after a write). */
export function withTicket(ts: CateringTicket[] | undefined, fresh: CateringTicket): CateringTicket[] | undefined {
  if (!ts) return ts
  return ts.map((t) => (t.plan_id === fresh.plan_id ? { ...fresh, lines: filterLinesLike(t, fresh) } : t))
}

/** Keep a station board's view: only the lines the list already showed. */
function filterLinesLike(shown: CateringTicket, fresh: CateringTicket) {
  const keys = new Set(shown.lines.map((l) => l.key))
  const kept = fresh.lines.filter((l) => keys.has(l.key))
  return kept.length > 0 ? kept : fresh.lines
}

/** Optimistic tick: the list with one line's status changed. */
export function withLineStatus(ts: CateringTicket[] | undefined, planId: string, key: string, status: CateringLineStatus): CateringTicket[] | undefined {
  if (!ts) return ts
  return ts.map((t) =>
    t.plan_id !== planId ? t : { ...t, lines: t.lines.map((l) => (l.key === key ? { ...l, status, changed: false } : l)) },
  )
}
